import { CalendarDays } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { displayDate } from '@/lib/dates'
import type { TimelineFilters } from './timelineFilters'

function localDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(0)
  date.setFullYear(year, month - 1, day)
  date.setHours(0, 0, 0, 0)
  return dateValue(date) === value ? date : undefined
}
function dateValue(date?: Date) {
  if (!date) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
export function timelineDatesLabel(filters: TimelineFilters) {
  if (!filters.from && !filters.to) return 'Dates'
  return `${dateLabel(filters.from)} – ${dateLabel(filters.to)}`
}
function dateLabel(value: string) {
  if (!value) return '…'
  // Preserve malformed URL text for correction; validation belongs to the API.
  return localDate(value) ? displayDate(value) : value
}
export function TimelineDates({
  filters,
  onChange,
  error,
}: {
  filters: TimelineFilters
  onChange: (filters: TimelineFilters) => void
  error?: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-9 gap-2"
          aria-label={`Dates: ${timelineDatesLabel(filters)}`}
        >
          <CalendarDays className="size-3.5 text-muted-foreground" />
          {timelineDatesLabel(filters)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[80vh] w-auto max-w-[calc(100vw-2rem)] overflow-auto"
      >
        <div className="flex flex-col gap-4 sm:flex-row">
          {(['from', 'to'] as const).map((key) => (
            <div key={key} role="group" aria-label={key === 'from' ? 'From date' : 'To date'}>
              <p className="pb-1 text-caption text-muted-foreground">
                {key === 'from' ? 'From' : 'To'}
              </p>
              <Calendar
                mode="single"
                selected={localDate(filters[key])}
                defaultMonth={localDate(filters[key])}
                onSelect={(date) => onChange({ ...filters, [key]: dateValue(date) })}
              />
            </div>
          ))}
        </div>
        {error && <p className="text-caption text-destructive">{error}</p>}
      </PopoverContent>
    </Popover>
  )
}
